/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useEffect, useState } from 'react';

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Tooltip,
	Avatar,
	Button,
	Stack,
} from '@mantine/core';

// Utils
import { truncateString } from '@utils/functions';

// Components
import { Onglet } from '@components/atoms/Onglet';
import Table from './components/Table/Table';

// Style
import classes from './DrawerProducer.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ProductionLogo from '@assets/images/production-logo.svg';
import { NOT_APPLICABLE } from '@config/constants/common';
import { TableAdditionalityProjects } from './components/TableAdditionalityProjects';

// Icons
import {
	IconEdit,
	IconQuestionMark,
	IconTrash,
} from '@tabler/icons-react';
import { VerifiedButton } from '@components/atoms';
import { getCountryName } from '@utils/countries';
import isNil from 'lodash/isNil';
import { useProjects } from '@api/get-projects';
import { TParsedProducer } from '../../types';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';
import { useProjectKpis } from '../../api/project-kpis';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TDrawerProducerProps = {
	opened: boolean;
	onClose: () => void;
	handledelete: () => void;
	handleupdate: (row: TParsedProducer) => void;
	data: TParsedProducer;
	kpisProjects: {
		id: string;
		name: string;
		kpis?: {
			SUM_OFFSET_AMOUNT_EURO: number;
			SUM_OFFSET_AMOUNT_GCO2: number;
		};
	}[];
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerProducer = (
	props: TDrawerProducerProps
) => {
	/* ******************************** PROPS ******************************* */

	const { opened, onClose, handledelete, kpisProjects } =
		props;

	/* ******************************** STATES ******************************* */
	const [activeTab, setActiveTab] = useState(
		'generalInformations'
	);

	const {
		organization,
		name,
		createdAt,
		updatedAt,
		legalIdentifier,
		verifiedBy,
		verifiedAt,
		contactName,
		contactEmail,
		contactPhone,
		location,
		verified,
		logo,
	} = props.data;
	/* ******************************** HELPERS ******************************* */
	const handleCloseDrawerShowMore = () => {
		setActiveTab('generalInformations');
		onClose();
	};

	/* ******************************** HOOKS ******************************* */

	const { additionalities, measurabilities } =
		useProjects(
			{ enabled: opened },
			{
				entity: props.data.id,
			}
		);

	const { t } = useTranslation();

	/* ******************************** CONSTANTS ******************************* */
	const { checkAcl, checkRole } = useAuthorization();
	const ongletItems = [
		{
			label: t('global.generalInformations'),
			value: 'generalInformations',
			id: 'id-generalInformations',
			nbr: null,
		},
		{
			label: t('global.details'),
			value: 'measurabilitiesProjects',
			id: '2',
			nbr:
				measurabilities &&
				Array.isArray(measurabilities) &&
				additionalities &&
				Array.isArray(additionalities)
					? measurabilities.length +
						additionalities.length
					: 0,
		},
	];

	/* ****************************** RENDERING ****************************** */

	if (props.data)
		return (
			<NativeDrawer
				onClose={handleCloseDrawerShowMore}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				position={'right'}
			>
				<Box className={classes.wrapper}>
					<Box className={classes.customHeader}>
						<Box className={classes.containerLogo}>
							<Image
								className={
									logo
										? classes.logoPicture
										: classes.logo
								}
								src={logo ? logo : ProductionLogo}
							/>
						</Box>
						<Box className={classes.containerTitle}>
							<Text className={classes.customSubTitle}>
								{t('global.producer')}
							</Text>
							{props.data.name && (
								<Text className={classes.customTitle}>
									{name}
								</Text>
							)}
						</Box>
					</Box>
					<Box className={classes.containerOnglet}>
						<Onglet
							items={ongletItems}
							onChange={(data: string | null) => {
								// eslint-disable-next-line @typescript-eslint/no-unused-expressions
								data !== null && setActiveTab(data);
							}}
						/>
					</Box>
					{/* Body */}
					<Box className={classes.customContent}>
						{activeTab == 'generalInformations' && (
							<>
								<Box className={classes.bodyDrawer}>
									{organization && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.organization')}
											</Text>
											<Tooltip
												label={organization}
												disabled={
													organization.length <= 20
												}
											>
												<Text className={classes.value}>
													{truncateString(
														organization,
														20
													)}
												</Text>
											</Tooltip>
										</Box>
									)}

									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.status')}
										</Text>

										<VerifiedButton
											readOnly
											data-testid={verified + '-testid'}
											isverified={
												verified
													? verified.toString()
													: undefined
											}
										>
											{verified
												? t('global.verified')
												: t('global.notVerified')}
										</VerifiedButton>
									</Box>
									<Box className={classes.row}>
										<Text className={classes.label}>
											{t('global.verifiedAt')}
										</Text>
										{verifiedBy !== NOT_APPLICABLE ? (
											<Text className={classes.value}>
												{verifiedAt}
											</Text>
										) : (
											<Text className={classes.label}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>

									{verifiedBy && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.verifiedBy')}
											</Text>

											{verifiedBy !== NOT_APPLICABLE ? (
												<Tooltip
													label={verifiedBy}
													disabled={
														verifiedBy.length <= 20
													}
												>
													<Text className={classes.value}>
														{truncateString(
															verifiedBy,
															20
														)}
													</Text>
												</Tooltip>
											) : (
												<Text className={classes.label}>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									)}
									{legalIdentifier && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.legalIdentifier')}
											</Text>
											{legalIdentifier !==
											NOT_APPLICABLE ? (
												<Text className={classes.value}>
													{legalIdentifier}
												</Text>
											) : (
												<Text className={classes.label}>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									)}
									{!isNil(location) && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.location')}
											</Text>
											<Box
												className={
													classes.groupCountryInformations
												}
											>
												<Avatar
													className={classes.logoCountry}
													radius={'xl'}
													src={`https://flagcdn.com/w40/${location?.countryCode?.toLowerCase()}.png`}
													size={'xs'}
												>
													<IconQuestionMark />
												</Avatar>
												<Tooltip
													label={`${getCountryName(location?.countryCode || '')} / ${location.place}`}
													disabled={
														location.countryCode &&
														location.place &&
														`${location.place}`.length >
															10
															? false
															: true
													}
												>
													<Box
														className={
															classes.groupCountryTextInformations
														}
													>
														<Text
															className={
																classes.textCountryProject
															}
														>
															{getCountryName(
																location?.countryCode ||
																	''
															)}
														</Text>

														<Text
															className={
																classes.textCountryPlaceProject
															}
														>
															{`/ ${location.place ? truncateString(location.place, 10) : NOT_APPLICABLE}`}
														</Text>
													</Box>
												</Tooltip>
											</Box>
										</Box>
									)}

									{contactName !== NOT_APPLICABLE &&
										contactName && (
											<Box className={classes.row}>
												<Text className={classes.label}>
													{t('global.contactName')}
												</Text>

												{contactName !==
												NOT_APPLICABLE ? (
													<Tooltip
														label={contactName}
														disabled={
															contactName.length <= 20
														}
													>
														<Text
															className={classes.value}
														>
															{truncateString(
																contactName,
																20
															)}
														</Text>
													</Tooltip>
												) : (
													<Text className={classes.label}>
														{NOT_APPLICABLE}
													</Text>
												)}
											</Box>
										)}

									{contactEmail !== NOT_APPLICABLE &&
										contactEmail && (
											<Box className={classes.row}>
												<Text className={classes.label}>
													{t('global.contactEmail')}
												</Text>

												{contactEmail !==
												NOT_APPLICABLE ? (
													<Tooltip
														label={contactEmail}
														disabled={
															contactEmail.length <= 20
														}
													>
														<Text
															className={classes.value}
														>
															{truncateString(
																contactEmail,
																20
															)}
														</Text>
													</Tooltip>
												) : (
													<Text className={classes.label}>
														{NOT_APPLICABLE}
													</Text>
												)}
											</Box>
										)}

									{contactPhone !== NOT_APPLICABLE &&
										contactPhone && (
											<Box className={classes.row}>
												<Text className={classes.label}>
													{t('global.contactPhone')}
												</Text>
												{contactPhone !==
												NOT_APPLICABLE ? (
													<Tooltip
														label={contactPhone}
														disabled={
															contactPhone.length <= 20
														}
													>
														<Text
															className={classes.value}
														>
															{truncateString(
																contactPhone,
																20
															)}
														</Text>
													</Tooltip>
												) : (
													<Text className={classes.label}>
														{NOT_APPLICABLE}
													</Text>
												)}
											</Box>
										)}

									{createdAt && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.createdAt')}
											</Text>
											<Text className={classes.value}>
												{createdAt}
											</Text>
										</Box>
									)}
									{updatedAt && (
										<Box className={classes.row}>
											<Text className={classes.label}>
												{t('global.updatedAt')}
											</Text>
											<Text className={classes.value}>
												{updatedAt}
											</Text>
										</Box>
									)}
								</Box>
							</>
						)}
						{activeTab == 'measurabilitiesProjects' && (
							<Box className={classes.containerTabSecond}>
								<Box className={classes.row}>
									<Text className={classes.title}>
										{t('projectDetails.measurabilities')}
									</Text>
								</Box>
								<Box style={{ height: '20px' }} />
								{/* Mesurability projects */}
								<Table
									kpisProjects={kpisProjects}
									rows={measurabilities}
								/>
								<Box style={{ height: '40px' }} />
								<Box className={classes.row}>
									<Text className={classes.title}>
										{t('projectDetails.additionalities')}
									</Text>
								</Box>
								<Box style={{ height: '20px' }} />
								{/* Additionality projects */}
								<TableAdditionalityProjects
									rows={additionalities}
								/>
							</Box>
						)}
					</Box>
					<Stack pt={'xs'} gap={'15px'}>
						{checkAcl(ACLS.PRODUCERS.UPDATE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Box className={classes.containerButton}>
									<Button
										leftSection={<IconEdit size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										onClick={() =>
											props.handleupdate(props.data)
										}
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('producers.updateProducer')}
									</Button>
									<Button
										leftSection={<IconEdit size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										onClick={() =>
											props.handleupdate(props.data)
										}
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('producers.updateProducer')}
									</Button>
								</Box>
							)}
						{checkAcl(ACLS.PRODUCERS.DELETE) &&
							checkRole([
								ROLES.ADMIN,
								ROLES.SUPERADMIN,
								ROLES.STAFF,
							]) && (
								<Box className={classes.containerButton}>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={16} />}
										hiddenFrom="xxl"
										size="sm"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('producers.deleteProducer')}
									</Button>
									<Button
										onClick={handledelete}
										leftSection={<IconTrash size={20} />}
										size="md"
										visibleFrom="xxl"
										fullWidth
										classNames={{
											root: classes.customDeleteButton,
										}}
									>
										{t('producers.deleteProducer')}
									</Button>
								</Box>
							)}
					</Stack>
				</Box>
			</NativeDrawer>
		);
};
